// EnsueLIVETRAK legal documents — single source of truth, rendered by <LegalContent />.
// Shown in the avatar dropdown (Terms & Conditions / Privacy Policy) and in the
// account-activation setup wizard (AcceptInvite). Keep LEGAL_VERSION in sync with the
// backend LEGAL_VERSION constant whenever these documents materially change.

export const LEGAL_VERSION = '2026-06-01';
export const LEGAL_EFFECTIVE_DATE = '01/06/2026';

export const TERMS_MARKDOWN = `# EnsueLIVETRAK ™ Terms & Conditions

**Website:** livetrack.ensue.com.au
**Platform:** EnsueLIVETRAK ™
**Effective date:** 01/06/2026
**Business name:** Ensue Pty Ltd
**ABN/ACN:** 71 618 864 476
**Contact email:** support@ensue.com.au

## 1. About These Terms

These Terms & Conditions apply to your access to and use of the EnsueLIVETRAK ™ website, web application, mobile applications, GPS tracking platform, related software, APIs, reports, notifications, live tracking services, historical tracking services, video telematics services, and associated products and services.

By accessing or using EnsueLIVETRAK ™, creating an account, logging in, adding devices, viewing vehicle data, sending commands, or using any part of the platform, you agree to be bound by these Terms. If you do not agree with these Terms, you must not use the platform.

## 2. Definitions

In these Terms:

* **“EnsueLIVETRAK ™”, “we”, “us”, or “our”** means Ensue Pty Ltd the owner and operator of the EnsueLIVETRAK ™ platform.
* **“Customer”, “you”, or “your”** means the person, business, company, organisation, fleet operator, reseller, contractor, employee, or authorised user accessing or using the platform.
* **“Platform”** means the EnsueLIVETRAK ™ GPS tracking software, dashboard, mobile apps, APIs, reports, notifications, alerts, video features, live tracking, history playback, remote media, and related services.
* **“Device”** means any GPS tracker, telematics unit, dash camera, MDVR, beacon, sensor, SIM-enabled device, or other hardware connected to or reporting data to the platform.
* **“Tracking Data”** means GPS location, speed, route history, ignition status, driver ID, RFID/ iButton data, event records, trip logs, telemetry, CAN bus data, sensor data, alerts, geofencing events, video events, images, timestamps, device diagnostics, and any other data transmitted by a device.
* **“User”** means any person authorised to access the platform, including administrators, managers, staff, drivers, customers, contractors, or end users.

## 3. Platform Purpose

EnsueLIVETRAK ™ is a GPS tracking and fleet management platform designed to assist customers with vehicle tracking, fleet visibility, driver management, asset monitoring, alerts, trip history, compliance support, video telematics, and related operational functions. The platform is intended to support business operations.

It is not a replacement for professional judgement, driver responsibility, workplace safety systems, legal compliance processes, emergency services, vehicle maintenance, or insurance requirements.

## 4. Account Registration and Access

To use the platform, you may be required to create an account or be invited by an administrator. You agree that all account information you provide must be accurate, complete, and kept up to date.

You are responsible for:
a. maintaining the confidentiality of your login details;
b. ensuring only authorised users access your account;
c. managing user permissions correctly;
d. removing users who no longer require access;
e. notifying us immediately if you suspect unauthorised access; and
f. all activity that occurs under your account.

We may suspend or restrict access if we reasonably believe an account is being misused, compromised, used unlawfully, or used in breach of these Terms.

## 5. Customer Responsibility for Lawful Tracking

You are solely responsible for ensuring that your use of EnsueLIVETRAK ™ and any connected devices is lawful. This includes ensuring that:
a. you have the legal right to install and use tracking devices;
b. drivers, employees, contractors, passengers, or other affected persons receive any required notices;
c. any required consent is obtained;
d. vehicle signage or device notices are displayed where required;
e. workplace surveillance laws are complied with;
f. privacy laws are complied with;
g. tracking is not used for unlawful, misleading, discriminatory, harassing, or covert purposes; and
h. tracking data is accessed only by authorised persons for legitimate business purposes.

You must not use the platform to secretly track a person, vehicle, asset, employee, contractor, family member, or any other individual without lawful authority.

## 6. Employee, Driver, and Workplace Surveillance

If you use EnsueLIVETRAK ™ to monitor employees, drivers, contractors, or workplace vehicles, you are responsible for complying with all applicable workplace surveillance, privacy, employment, industrial relations, and vehicle tracking laws. Depending on your location and circumstances, this may require:
a. written notice before tracking begins;
b. a workplace surveillance policy;
c. driver or employee acknowledgement;
d. visible tracking notices on vehicles or tracked assets;
e. explaining what data is collected;
f. explaining when tracking is active;
g. explaining whether tracking is continuous or intermittent;
h. explaining who may access the data;
i. explaining how data may be used; and
j. maintaining records of notices and consent.

In NSW, the Workplace Surveillance Act 2005 requires prior written notice for workplace surveillance and includes additional requirements for tracking surveillance involving vehicles or other things. You should obtain legal advice about the rules that apply in each Australian state or territory where you operate and if outside Australia you must comply with that country or territory applicable laws.

## 7. Privacy and Personal Information

Your use of the platform may involve the collection, storage, use, and disclosure of personal information, including location data, driver information, user account information, vehicle usage information, images, video, and event records. You agree to comply with all applicable privacy laws, including the Privacy Act 1988, the Australian Privacy Principles where applicable, and any other privacy obligations relevant to your business.

The Australian Privacy Principles regulate the collection, use, disclosure, storage, access, correction, and security of personal information for organisations covered by the Privacy Act. APP 11 requires covered entities to take reasonable steps to protect personal information from misuse, interference, loss, unauthorised access, modification, or disclosure.

You must ensure that any person whose personal information is collected through the platform is provided with appropriate privacy notices. These Terms should be read together with our Privacy Policy.

## 8. Tracking Data and Data Ownership

As between you and us, you retain ownership of your business data and tracking data, subject to these Terms.

You grant us a licence to collect, receive, process, store, transmit, back up, display, analyse, and use tracking data as reasonably necessary to provide, maintain, secure, support, and improve the platform.

We may use aggregated, anonymised, or de-identified data for analytics, diagnostics, security, performance monitoring, product improvement, and reporting, provided it does not reasonably identify you, your users, your drivers, or your customers.

## 9. GPS Accuracy and Device Limitations

You acknowledge that GPS tracking and telematics data may not always be accurate, complete, current, or available. Accuracy and availability may be affected by factors including:
a. GPS satellite availability;
b. cellular network coverage;
c. SIM card issues;
d. device firmware;
e. device installation quality;
f. antenna placement;
g. tunnels, buildings, basements, underground car parks, and dense urban areas;
h. power loss or disconnection;
i. vehicle electrical faults;
j. device tampering;
k. data delays;
l. third-party network outages;
m. map provider errors; and
n. software or configuration issues.

You must not rely on the platform as the sole source of truth for safety-critical decisions, emergency response, payroll disputes, legal proceedings, driver discipline, or compliance enforcement without independent verification.

## 10. Alerts, Notifications, and Reports

The platform may provide alerts, notifications, emails, SMS messages, push notifications, geofence alerts, speed alerts, ignition alerts, driver behaviour alerts, maintenance reminders, video event alerts, and other automated notifications.

You acknowledge that alerts may be delayed, missed, duplicated, incorrect, incomplete, or unavailable. Notifications depend on many factors outside our control, including mobile networks, internet connectivity, device status, third-party services, email providers, SMS gateways, mobile operating systems, user permissions, and customer configuration.

You are responsible for checking alerts, configuring thresholds correctly, and verifying important events independently. The platform must not be used as an emergency monitoring service unless we expressly agree in writing.

## 11. Video Telematics, Dash Cameras, and Media

Where supported, EnsueLIVETRAK ™ may allow customers to access live video, event videos, remote downloads, snapshots, audio, camera channels, and other media captured by compatible devices.

You are responsible for ensuring that any use of cameras, audio recording, in-cabin cameras, external cameras, event video, live streaming, and video downloads complies with applicable privacy, surveillance, workplace, road transport, and employment laws. You must ensure that affected persons are informed where required, including drivers, employees, passengers, contractors, and members of the public where applicable.

We do not guarantee that video will always be available, playable, complete, downloadable, or stored indefinitely. Video availability may depend on device settings, SD card condition, camera channels, firmware, network coverage, upload configuration, cloud storage, retention settings, and device power.

## 12. Remote Commands and Device Control

The platform may allow authorised users to send commands to devices, including configuration updates, output commands, immobiliser-related commands, camera commands, live stream requests, restart commands, and other supported commands.

You are responsible for ensuring commands are used safely, lawfully, and only by trained authorised users. You must not send commands that may create a safety risk, interfere with a vehicle while it is moving, breach road laws, damage equipment, or endanger any person.

We may restrict, log, delay, reject, or disable certain command functions for safety, compliance, technical, or security reasons.

## 13. Customer Hardware and Installation

Unless otherwise agreed in writing, you are responsible for:
a. purchasing compatible hardware;
b. correct installation;
c. vehicle wiring;
d. antenna placement;
e. SIM card activation;
f. mobile network coverage;
g. firmware updates;
h. device configuration;
i. device maintenance;
j. replacing faulty devices; and
k. ensuring devices are used in accordance with manufacturer instructions.

Incorrect installation may result in inaccurate data, vehicle faults, battery drain, device failure, loss of tracking, or safety issues. Any installation should be performed by a suitably qualified person.

## 14. Acceptable Use

You must not use the platform to:
a. break any law;
b. unlawfully track or monitor any person;
c. stalk, harass, intimidate, discriminate against, or harm any person;
d. access data you are not authorised to access;
e. interfere with the platform or its security;
f. attempt to reverse engineer, copy, or misuse the software;
g. upload malicious code;
h. overload or attack the platform;
i. bypass permission controls or feature restrictions;
j. share login credentials;
k. use the platform for unlawful surveillance;
l. use tracking data in a misleading or unfair way; or
m. resell, sublicense, or provide access to the platform unless authorised in writing.

## 15. User Permissions and Administration

Customers are responsible for assigning appropriate access levels to their users. You must ensure that users only receive access to the vehicles, drivers, reports, camera feeds, commands, data, and functions they are authorised to use.

We are not responsible for loss, misuse, privacy breaches, unauthorised viewing, or incorrect actions caused by your user permission settings, shared logins, weak passwords, or failure to remove former users.

## 16. Subscription, Fees, and Payment

Fees, billing frequency, included features, device limits, storage limits, support levels, and subscription terms will be set out in your order form, quote, invoice, service agreement, plan, or other written agreement.

You agree to pay all applicable fees when due. If payment is overdue, we may suspend or restrict access after providing reasonable notice, unless immediate suspension is required due to risk, fraud, misuse, or legal obligation.

Fees are exclusive of GST unless stated otherwise.

## 17. Plan Limits and Feature Availability

Your access to features may depend on your subscription plan, enabled modules, device type, hardware capability, firmware, permissions, and configuration. Certain features may be unavailable, limited, experimental, subject to additional fees, or dependent on third-party services.

We may add, remove, modify, improve, or replace features from time to time, provided we do not materially reduce the core services you have paid for without reasonable notice.

## 18. Data Retention

Unless otherwise agreed in writing, tracking history, reports, videos, images, logs, and other data may be retained only for a limited period. For standard GPS tracking history, our retention period is dependent on you plan and may be up to 365 days, unless your plan or agreement states otherwise.

After the retention period expires, data may be automatically deleted and may not be recoverable. You are responsible for exporting or downloading any reports, history, videos, or records you require before expiry.

Certain audit logs, billing records, security logs, or legal records may be retained for longer where required for compliance, security, dispute resolution, or legal purposes.

## 19. Backups and Data Recovery

We may perform backups for operational, security, or disaster recovery purposes. Backups are not a substitute for your own recordkeeping obligations. We do not guarantee that deleted, expired, overwritten, or corrupted data can be restored.

## 20. Security

We will take reasonable steps to protect the platform and data against unauthorised access, misuse, interference, loss, modification, and disclosure. However, no system is completely secure.

You are responsible for:
a. using strong passwords;
b. enabling multi-factor authentication where available;
c. protecting login credentials;
d. managing user access;
e. keeping devices and browsers updated;
f. securing your own systems; and
g. reporting suspected security issues promptly.

## 21. Third-Party Services

The platform may rely on third-party services including cloud hosting providers, map providers, SMS providers, email providers, mobile network operators, SIM providers, push notification services, video storage providers, payment processors, device manufacturers, and APIs.

We are not responsible for outages, errors, delays, pricing changes, data issues, map inaccuracies, network failures, or service changes caused by third-party providers. Your use of third-party features may also be subject to the relevant third party’s terms and policies.

## 22. Maps, Routes, and Geocoding

Maps, addresses, geocoding, road names, speed zones, traffic information, and route displays may be supplied by third-party providers. Map data may be incomplete, outdated, inaccurate, or unavailable.

You must not rely solely on map displays or geocoded addresses for emergency response, legal enforcement, route compliance, payroll, safety decisions, or driver discipline without independent verification.

## 23. Maintenance and Availability

We aim to provide a reliable platform, but we do not guarantee uninterrupted or error-free operation. The platform may be unavailable due to maintenance, upgrades, security patches, outages, cloud provider issues, internet failures, cyber incidents, database issues, third-party failures, or circumstances beyond our control.

Where practical, we may provide notice of planned maintenance.

## 24. Support

Support may be provided by email, phone, ticketing system, chat, or another method we nominate. Support availability, response times, and included services may depend on your subscription plan or agreement.

Support does not include unrelated IT support, hardware installation, vehicle electrical work, SIM management, device replacement, custom development, or third-party system troubleshooting unless agreed in writing.

## 25. Customer Responsibilities

You are responsible for:
a. ensuring lawful use of the platform;
b. obtaining all required consents and notices;
c. maintaining accurate customer, vehicle, driver, and device records;
d. ensuring devices are correctly installed and configured;
e. ensuring users are trained;
f. managing access permissions;
g. complying with privacy and workplace laws;
h. reviewing alerts and reports appropriately;
i. maintaining your own internal policies;
j. ensuring drivers are aware of monitoring where required;
k. ensuring tracking data is not misused; and
l. keeping payment details current.

## 26. Intellectual Property

EnsueLIVETRAK ™, including the platform, website, software, design, user interface, logos, trademarks, graphics, database structure, documentation, reports, workflows, and related materials, is owned by us or licensed to us.

You must not copy, reproduce, modify, distribute, reverse engineer, scrape, resell, or create derivative works from the platform unless authorised in writing. Nothing in these Terms transfers ownership of our intellectual property to you.

## 27. API Access and Integrations

Where API access is provided, you must use it only for authorised business purposes and in accordance with any API documentation, rate limits, security requirements, and integration rules.

We may suspend or limit API access if it creates security risks, excessive load, misuse, data leakage, or breach of these Terms. You are responsible for any third-party systems you connect to the platform.

## 28. Confidentiality

Each party must keep confidential information confidential and must not disclose it except where required for the platform, authorised by the other party, required by law, or disclosed to professional advisers under confidentiality obligations.

Confidential information includes pricing, technical information, business information, customer records, security information, platform credentials, non-public software details, and sensitive operational data.

## 29. Australian & International Consumer Law

Nothing in these Terms excludes, restricts, or modifies any consumer guarantee, right, remedy, or protection that cannot lawfully be excluded under Australian Consumer Law or other applicable International law.

Where we are permitted to limit our liability, our liability is limited, at our option, to resupplying the services or paying the cost of having the services supplied again.

Standard form contracts with consumers and small businesses are subject to unfair contract term protections under Australian Consumer Law. These Terms should be reviewed to ensure they are fair, transparent, and appropriate for your customer base.

## 30. Limitation of Liability

To the maximum extent permitted by law, we are not liable for:
a. loss caused by inaccurate GPS data;
b. lost tracking history;
c. unavailable video;
d. device failure;
e. SIM or network failure;
f. map errors;
g. customer configuration errors;
h. unauthorised access caused by customer-side security failures;
i. misuse of tracking data by your users;
j. driver behaviour;
k. vehicle theft, damage, accident, or loss;
l. failure of alerts or notifications;
m. business interruption;
n. loss of profit;
o. loss of revenue;
p. loss of goodwill;
q. indirect or consequential loss; or
r. matters outside our reasonable control.

## 31. Indemnity

You agree to indemnify us against claims, losses, liabilities, costs, damages, and expenses arising from:
a. your unlawful use of the platform;
b. failure to obtain required notices or consents;
c. breach of privacy or workplace surveillance laws;
d. misuse of tracking data by your users;
e. unauthorised device installation;
f. unsafe or unlawful use of remote commands;
g. breach of these Terms;
h. third-party claims from drivers, employees, contractors, passengers, or customers; and
i. data or content you upload, transmit, or store using the platform.

## 32. Suspension and Termination

We may suspend or terminate access if:
a. you breach these Terms;
b. fees are overdue;
c. your use creates security, legal, operational, or reputational risk;
d. you use the platform unlawfully;
e. you misuse tracking, video, or personal information;
f. required by law; or
g. continued access may harm us, the platform, another customer, or any person.

You may cancel your subscription in accordance with your service agreement, plan terms, or written agreement.

After termination, access to the platform may cease and data may be deleted after any applicable retention period.

## 33. Resellers and Sub-Accounts

If you are a reseller, dealer, installer, fleet manager, or administrator managing accounts for other customers, you are responsible for ensuring that your customers and end users comply with these Terms.

You must not make promises, warranties, or representations about the platform that we have not authorised in writing. You are responsible for managing sub-accounts, customer access, billing arrangements, permissions, support obligations, and lawful use by your customers.

## 34. Beta Features and Trial Features

We may offer beta, trial, experimental, or early-access features. These features may be incomplete, unstable, changed, removed, or unavailable at any time. Beta features are provided “as is” and should not be used for critical operations unless we expressly agree in writing.

## 35. Changes to These Terms

We may update these Terms from time to time. If changes are material, we will take reasonable steps to notify customers, such as by email, platform notice, or website notice.

Continued use of the platform after updated Terms take effect means you accept the updated Terms.

## 36. Governing Law

These Terms are governed by the laws of New South Wales, Australia, unless another jurisdiction is specified in your written agreement.

The parties submit to the non-exclusive jurisdiction of the courts of New South Wales and the Commonwealth courts of Australia.

## 37. Contact

For questions about these Terms, please contact:

**EnsueLIVETRAK ™ Support**
Email: support@ensue.com.au
Website: livetrack.ensue.com.au
Business: Ensue Pty Ltd
ABN/ACN: 71 618 864 476`;

export const PRIVACY_MARKDOWN = `# Privacy Policy

EnsueLIVETRAK ™
Effective date: 01/06/2026

Website: livetrack.ensue.com.au
Platform: EnsueLIVETRAK ™
Company: Ensue Pty Ltd
ABN: 71 618 864 476
Contact email: support@ensue.com.au

## 1. Introduction

Ensue Pty Ltd, ABN 71 618 864 476, operates the EnsueLIVETRAK ™ GPS tracking, fleet management, telematics, and video telematics platform. This Privacy Policy explains how we collect, use, store, disclose, and protect personal information when you access our website, use our platform, create an account, connect GPS tracking devices, use fleet management features, view vehicle location data, access reports, receive alerts, or use any related services. By using EnsueLIVETRAK ™, you agree to the handling of information as described in this Privacy Policy. This Privacy Policy should be read together with our Terms & Conditions and any Customer Service Agreement between you and Ensue Pty Ltd.

## 2. What Information We Collect

We may collect and process different types of information depending on how you use EnsueLIVETRAK ™.

### 2.1 Customer and Account Information

We may collect:
a. business name;
b. contact name;
c. email address;
d. phone number;
e. billing details;
f. account username;
g. user role and permissions;
h. company address;
i. support communications;
j. subscription and plan details; and
k. account activity records.

### 2.2 User Information

For users of the platform, we may collect:
a. name;
b. email address;
c. mobile number;
d. login details;
e. user role;
f. permissions;
g. time zone and regional settings;
h. audit logs;
i. login history;
j. IP address;
k. browser and device information; and
l. actions performed inside the platform.

### 2.3 Driver, Staff, and Fleet Personnel Information

Where customers use EnsueLIVETRAK ™ to manage drivers, staff, passengers, or contractors, the platform may store:
a. driver name;
b. staff name;
c. contact details;
d. driver ID;
e. RFID, iButton, BLE beacon, or other identification number;
f. driver assignment history;
g. trip history;
h. vehicle usage records;
i. driving behaviour events;
j. speed events;
k. harsh braking, harsh acceleration, harsh cornering, or incident events;
l. fatigue or safety-related events, where supported; and
m. other fleet management information entered by the customer.

### 2.4 Vehicle, Asset, and Device Information

We may collect and process information about vehicles, trailers, plant, machinery, equipment, or assets connected to the platform, including:
a. vehicle registration;
b. make, model, and year;
c. VIN or chassis number;
d. odometer;
e. engine hours;
f. fuel information;
g. device IMEI or serial number;
h. SIM card information;
i. ICCID;
j. device type and model;
k. firmware version;
l. installation information;
m. sensor and input/output data;
n. CAN bus or vehicle telemetry data, where supported; and
o. maintenance and service records.

### 2.5 GPS, Location, and Telematics Data

EnsueLIVETRAK ™ may collect and process tracking and telematics data from connected devices, including:
a. GPS location;
b. latitude and longitude;
c. speed;
d. heading;
e. altitude;
f. route history;
g. ignition status;
h. movement status;
i. trip start and stop times;
j. geofence entry and exit events;
k. idle time;
l. stop duration;
m. distance travelled;
n. timestamps;
o. device online/offline status;
p. GPS signal status;
q. GSM or network signal status;
r. battery status;
s. sensor events; and
t. alarms generated by supported devices.

### 2.6 Video, Image, and Audio Data

Where supported devices are connected, EnsueLIVETRAK ™ may process:
a. event videos;
b. dash camera videos;
c. live video streams;
d. remote video downloads;
e. snapshots;
f. thumbnails;
g. in-cabin camera footage, where enabled;
h. external camera footage;
i. audio, where enabled and lawful;
j. event metadata;
k. camera channel information; and
l. upload and storage logs.

Customers are responsible for ensuring any video, audio, or surveillance features are used lawfully and with all required notices and consents.

### 2.7 Technical and Usage Information

When you use our website or platform, we may collect:
a. IP address;
b. browser type;
c. device type;
d. operating system;
e. pages accessed;
f. session activity;
g. error logs;
h. performance logs;
i. security logs;
j. API usage logs;
k. date and time of access; and
l. diagnostic information.

## 3. How We Collect Information

We may collect information:
a. directly from you;
b. from your employer or organisation;
c. from platform administrators;
d. from connected GPS tracking devices;
e. from dash cameras, MDVRs, sensors, and telematics units;
f. when you log into or use the platform;
g. when you contact support;
h. when you submit forms;
i. when alerts, reports, or logs are generated;
j. from third-party services connected to the platform; and
k. from device manufacturers, installers, or resellers where relevant.

## 4. Why We Collect and Use Information

We collect and use information to:
a. provide the EnsueLIVETRAK ™ platform;
b. create and manage customer accounts;
c. display live vehicle and asset locations;
d. provide trip history and playback;
e. generate reports;
f. manage drivers, staff, vehicles, trailers, and assets;
g. provide geofencing features;
h. send alerts and notifications;
i. process video telematics events;
j. provide live video and remote media features where enabled;
k. send commands to supported devices;
l. provide technical support;
m. diagnose faults;
n. improve platform performance;
o. secure the platform;
p. maintain audit trails;
q. prevent misuse or unauthorised access;
r. manage billing and subscriptions;
s. comply with legal obligations; and
t. enforce our agreements and policies.

## 5. Customer Responsibility for Lawful Tracking

Customers are responsible for ensuring that their use of EnsueLIVETRAK ™ is lawful. This includes ensuring that:
a. tracking devices are installed lawfully;
b. vehicles and assets are lawfully monitored;
c. drivers, staff, contractors, and affected persons receive required notices;
d. consents are obtained where required;
e. workplace surveillance laws are complied with;
f. privacy laws are complied with;
g. video and audio recording is used lawfully;
h. tracking data is accessed only by authorised persons; and
i. the platform is not used for unlawful, covert, misleading, harassing, or discriminatory monitoring.

Ensue Pty Ltd provides the platform, but customers remain responsible for their own legal compliance when using GPS tracking, driver monitoring, vehicle surveillance, video telematics, and fleet reporting features.

## 6. Workplace Tracking, Driver Monitoring, and Surveillance

If a customer uses EnsueLIVETRAK ™ to monitor employees, drivers, contractors, or workplace vehicles, the customer must ensure that all required workplace notices, policies, consents, and vehicle notices are in place. Depending on the jurisdiction, this may include informing workers:
a. that GPS tracking is being used;
b. what vehicles, devices, or assets are being tracked;
c. when tracking occurs;
d. what information is collected;
e. how the information may be used;
f. who can access the information;
g. whether video or audio is recorded; and
h. how long records are retained.

Customers should obtain their own legal advice about workplace surveillance and privacy obligations.

## 7. Disclosure of Information

We may disclose information to:
a. authorised users within your organisation;
b. platform administrators;
c. cloud hosting providers;
d. data storage providers;
e. email service providers;
f. SMS and push notification providers;
g. map and geocoding providers;
h. software and infrastructure providers;
i. device manufacturers or technical support providers where required;
j. installers or resellers where authorised;
k. payment and billing providers;
l. professional advisers;
m. insurers;
n. regulators, courts, law enforcement, or government agencies where required by law; and
o. other parties with your consent or as required to provide the platform.

We do not sell personal information.

## 8. Overseas Disclosure

Some of our technology providers, support providers, cloud infrastructure providers, or communications providers may store, process, or access information outside Australia. Where information is disclosed overseas, we will take reasonable steps to ensure it is handled appropriately and in accordance with applicable privacy obligations. Customers should consider whether their own privacy notices need to disclose overseas storage or processing.

## 9. Data Security

We take reasonable steps to protect information from misuse, interference, loss, unauthorised access, modification, and disclosure. Security measures may include:
a. encrypted connections;
b. access controls;
c. user permissions;
d. audit logs;
e. secure hosting;
f. server monitoring;
g. password protection;
h. token-based authentication;
i. firewall and infrastructure controls;
j. backups; and
k. internal access restrictions.

However, no online system, GPS platform, cloud service, or communication network is completely secure. Customers are responsible for maintaining strong passwords, managing user permissions, removing former users, protecting devices, and notifying us of suspected unauthorised access.

## 10. Data Retention

Unless otherwise agreed in writing:
a. standard GPS tracking history may be retained for up to 180 days;
b. video and image retention may depend on the customer’s plan, device settings, storage configuration, or available storage;
c. audit logs and security logs may be retained for longer where required for compliance, security, or dispute resolution;
d. billing and account records may be retained as required by law; and
e. expired or deleted data may not be recoverable.

Customers are responsible for exporting any reports, trip history, videos, or records they require before the retention period expires.

## 11. Access and Correction

You may request access to personal information we hold about you. You may also request correction of information that is inaccurate, out of date, incomplete, irrelevant, or misleading. Requests can be sent to: support@ensue.com.au

We may need to verify your identity before responding. If the information is controlled by one of our customers, such as an employer or fleet operator, we may refer the request to that customer where appropriate.

## 12. Data Breaches

If we become aware of a data breach that is likely to result in serious harm, we will assess the incident and take steps required by applicable law. This may include notifying affected individuals, customers, or regulators where required. Customers must promptly notify us if they become aware of unauthorised access, misuse, disclosure, or loss of information connected to their EnsueLIVETRAK ™ account.

## 13. Cookies and Website Analytics

Our website and platform may use cookies, local storage, session storage, analytics tools, and similar technologies to:
a. keep users logged in;
b. remember preferences;
c. improve performance;
d. measure usage;
e. detect errors;
f. improve security; and
g. support platform functionality.

You may disable cookies in your browser, but some platform features may not work correctly.

## 14. Third-Party Websites and Services

The platform may contain links or integrations with third-party services. We are not responsible for the privacy practices, security, content, or policies of third-party websites or services. Customers and users should review the privacy policies of any third-party services they use.

## 15. Children

EnsueLIVETRAK ™ is intended for business, fleet, asset, and operational use. It is not intended for use by children. Customers must not knowingly use the platform to unlawfully track children or minors unless in special circumstances.

## 16. Changes to This Privacy Policy

We may update this Privacy Policy from time to time. If we make material changes, we may notify customers by email, platform notice, or website notice. The updated version will apply from the effective date shown at the top of the policy.

## 17. Contact Us

For privacy questions, access requests, correction requests, complaints, or data protection concerns, contact:

Ensue Pty Ltd
ABN: 71 618 864 476
Platform: EnsueLIVETRAK ™
Website: livetrack.ensue.com.au
Email: support@ensue.com.au`;
